import { defineStore } from 'pinia'
import { fetchUserSettings, saveUserSettings } from '@/api/settings'

// 서버 layer (code + f_type) ↔ 내부 key 매핑 — 레거시 응답을 내부 키 기반으로 정규화
//   서버: { code: 0, f_type: 'Service', ... } / { code: 7, ... }
//   내부: 'service' / 'block' (settings.layerByKey 로 조회 가능)
const KEY_TO_CODE = {
  block:                    { code: 7 },
  service:                  { code: 0, f_type: 'Service' },
  supply:                   { code: 0, f_type: 'Supply' },
  valve:                    { code: 1 },
  meter:                    { code: 2 },
  pump:                     { code: 3 },
  hydrant:                  { code: 4 },
  junction:                 { code: 5 },
  water_treatment_plant:    { code: 8 },
  pumping_station:          { code: 9 },
  water_reservoir:          { code: 10 },
  chamber:                  { code: 11 },
  gauge:                    { code: 12 },
  other:                    { code: 13 },
}
function codeKey(code, fType) {
  for (const [k, v] of Object.entries(KEY_TO_CODE)) {
    if (v.code === Number(code) && (v.f_type ?? null) === (fType ?? null)) return k
  }
  return null
}

// 각 레이어가 code + f_type 을 직접 보유 — 지도 fetch (call_type=1, layer={code}, f_type={f_type}) 가 이 값을 사용
const DEFAULT_LAYERS = [
  { key: 'block',                 code: 7,                    label: '블럭',                  zoom: 13, daum_minzoom: 7,  bing_minzoom: 13, color: '#edf2b1', visible: true },
  { key: 'service',               code: 0, f_type: 'Service', label: '공급관 (Service)',     zoom: 15, daum_minzoom: 9,  bing_minzoom: 15, color: '#2800f0', visible: true },
  { key: 'supply',                code: 0, f_type: 'Supply',  label: '송수관 (Supply)',      zoom: 13, daum_minzoom: 7,  bing_minzoom: 13, color: '#57b6ff', visible: true },
  { key: 'valve',                 code: 1,                    label: '밸브',                  zoom: 15, daum_minzoom: 9,  bing_minzoom: 15, color: '#f00000', visible: true },
  { key: 'meter',                 code: 2,                    label: '수도계량기',            zoom: 15, daum_minzoom: 9,  bing_minzoom: 15, color: '#00f05c', visible: true },
  { key: 'pump',                  code: 3,                    label: '펌프',                  zoom: 15, daum_minzoom: 9,  bing_minzoom: 15, color: '#323232', visible: false },
  { key: 'hydrant',               code: 4,                    label: '소화전',                zoom: 15, daum_minzoom: 9,  bing_minzoom: 15, color: '#fa0000', visible: true },
  { key: 'junction',              code: 5,                    label: 'Junction',              zoom: 15, daum_minzoom: 9,  bing_minzoom: 15, color: '#9933FF', visible: false },
  { key: 'water_treatment_plant', code: 8,                    label: 'Water Treatment Plant', zoom: 15, daum_minzoom: 9,  bing_minzoom: 15, color: '#99FFFF', visible: false },
  { key: 'pumping_station',       code: 9,                    label: 'Pumping Station',       zoom: 15, daum_minzoom: 9,  bing_minzoom: 15, color: '#CCFFE5', visible: true },
  { key: 'water_reservoir',       code: 10,                   label: 'Water Reservoir',       zoom: 15, daum_minzoom: 9,  bing_minzoom: 15, color: '#FF3399', visible: true },
  { key: 'chamber',               code: 11,                   label: 'Chamber',               zoom: 15, daum_minzoom: 9,  bing_minzoom: 15, color: '#c9d9e8', visible: true },
  { key: 'gauge',                 code: 12,                   label: 'Gauge',                 zoom: 15, daum_minzoom: 9,  bing_minzoom: 15, color: '#FF8000', visible: true },
  { key: 'other',                 code: 13,                   label: 'Other',                 zoom: 15, daum_minzoom: 9,  bing_minzoom: 15, color: '#000000', visible: false },
]

const DEFAULT_WAVE = {
  freqXMax: 2000, freqYMax: 100,
  energyMin: 5, energyMax: 50, energyFixed: 10,
  leakColor: '#ef4444', otherColor: '#34d399',
  // 레거시 visible_wave_energy — 마커 옆에 wave energy 값 표시 토글
  valueVisible: false,
}

const DEFAULT_PRESSURE = {
  min: 0, max: 10, color: '#22d3ee',
  // 레거시 legend.value (min/max/avg) — 압력 마커 크기 계산 기준값
  value: 'avg',
  // 레거시 visible_value — 마커 옆에 압력 값 표시 토글
  valueVisible: false,
}

// 레거시 setting.map_name / map_type / extent 매핑 — 배경지도 기본 설정
const DEFAULT_MAP = {
  name: 'Bing',          // 'Daum' | 'Bing'
  type: 'Standard',      // 'Standard' | 'Satellite'
  extent: [124.5, 33.0, 131.5, 39.5], // 한국 대략 (minX, minY, maxX, maxY) — EPSG:4326
}

export const useSettingsStore = defineStore('settings', {
  state: () => ({
    loaded: false,
    loading: false,
    error: '',
    layers:   DEFAULT_LAYERS.map(l => ({ ...l })),
    wave:     { ...DEFAULT_WAVE },
    pressure: { ...DEFAULT_PRESSURE },
    map:      { ...DEFAULT_MAP, extent: [...DEFAULT_MAP.extent] },
  }),
  getters: {
    layerByKey: (state) => {
      const map = {}
      state.layers.forEach(l => { map[l.key] = l })
      return map
    },
    visibleLayerKeys: (state) => state.layers.filter(l => l.visible).map(l => l.key),
  },
  actions: {
    applyServerData(data) {
      if (!data) return
      // ── layers: 새 형태({key, ...}) 또는 레거시 형태({code, f_type, ...}) 모두 처리 ──
      if (Array.isArray(data.layers)) {
        const byKey = new Map()
        data.layers.forEach(l => {
          const key = l.key ?? codeKey(l.code, l.f_type)
          if (!key) return
          // 서버 응답: code, f_type, daum_minzoom, bing_minzoom 모두 보존
          const zoom = l.zoom ?? l.daum_minzoom ?? l.bing_minzoom
          byKey.set(key, {
            code:         l.code,
            f_type:       l.f_type,
            label:        l.label ?? l.name,
            zoom:         zoom != null ? Number(zoom) : undefined,
            daum_minzoom: l.daum_minzoom != null ? Number(l.daum_minzoom) : undefined,
            bing_minzoom: l.bing_minzoom != null ? Number(l.bing_minzoom) : undefined,
            color:        l.color,
            visible:      l.visible ?? l.checked,
          })
        })
        this.layers = DEFAULT_LAYERS.map(def => {
          const s = byKey.get(def.key)
          if (!s) return { ...def }
          // bing_minzoom 결정 — 서버 값이 없거나 daum_minzoom 과 동일하면(레거시 클론 케이스)
          //   DEFAULT_LAYERS 의 bing_minzoom 을 사용해 daum 값과 구분되도록 표시
          const sBing = (s.bing_minzoom != null && s.bing_minzoom !== s.daum_minzoom)
            ? s.bing_minzoom
            : def.bing_minzoom
          return {
            ...def,
            code:         s.code         ?? def.code,
            f_type:       s.f_type       ?? def.f_type,
            label:        s.label        ?? def.label,
            zoom:         s.zoom         ?? def.zoom,
            daum_minzoom: s.daum_minzoom ?? def.daum_minzoom,
            bing_minzoom: sBing,
            color:        s.color        ?? def.color,
            visible:      s.visible      ?? def.visible,
          }
        })
      }
      // ── wave: 레거시 nested ({frequency, wave_energy, color, color1, visible_wave_energy})
      //    또는 내부 flat ({freqXMax, energyMin, ..., leakColor, otherColor, valueVisible}) 모두 처리 ──
      if (data.wave) {
        const w = data.wave
        const fx = w.frequency?.x ?? w.freqXMax
        const fy = w.frequency?.y ?? w.freqYMax
        const eMin = w.wave_energy?.start_size ?? w.energyMin
        const eMax = w.wave_energy?.end_size   ?? w.energyMax
        const eFix = w.wave_energy?.fix_size   ?? w.energyFixed
        const otherColor = w.color  ?? w.otherColor
        const leakColor  = w.color1 ?? w.leakColor
        const valueVisible = w.visible_wave_energy ?? w.valueVisible
        this.wave = {
          ...this.wave,
          ...(fx != null   ? { freqXMax:    Number(fx) }   : {}),
          ...(fy != null   ? { freqYMax:    Number(fy) }   : {}),
          ...(eMin != null ? { energyMin:   Number(eMin) } : {}),
          ...(eMax != null ? { energyMax:   Number(eMax) } : {}),
          ...(eFix != null ? { energyFixed: Number(eFix) } : {}),
          ...(otherColor   ? { otherColor }                : {}),
          ...(leakColor    ? { leakColor  }                : {}),
          ...(valueVisible != null ? { valueVisible: !!valueVisible } : {}),
        }
      }
      // ── pressure: 레거시 nested ({legend, color, visible_value}) 또는 내부 flat 모두 처리 ──
      if (data.pressure) {
        const p = data.pressure
        const min   = p.legend?.start_size ?? p.min
        const max   = p.legend?.end_size   ?? p.max
        const value = p.legend?.value      ?? p.value
        const color = p.color
        const valueVisible = p.visible_value ?? p.valueVisible
        this.pressure = {
          ...this.pressure,
          ...(min   != null ? { min:   Number(min) } : {}),
          ...(max   != null ? { max:   Number(max) } : {}),
          ...(value != null ? { value }              : {}),
          ...(color         ? { color }              : {}),
          ...(valueVisible != null ? { valueVisible: !!valueVisible } : {}),
        }
      }
      // ── map: 새 형태(data.map.{name,type,extent}) 또는 레거시 평탄(data.map_name/map_type/extent) ──
      const legacyMap = (data.map_name != null || data.map_type != null || Array.isArray(data.extent))
        ? { name: data.map_name, type: data.map_type, extent: data.extent }
        : null
      const m = data.map || legacyMap
      if (m) this.map = {
        ...this.map,
        ...(m.name != null ? { name: m.name } : {}),
        ...(m.type != null ? { type: m.type } : {}),
        extent: Array.isArray(m.extent) && m.extent.length === 4
          ? m.extent.map(Number)
          : [...this.map.extent],
      }
    },
    applyLocal(payload) {
      if (!payload) return
      if (Array.isArray(payload.layers)) {
        const map = new Map(payload.layers.map(l => [l.key, l]))
        this.layers = DEFAULT_LAYERS.map(def => {
          const s = map.get(def.key)
          return s ? { ...def, ...s } : { ...def }
        })
      }
      if (payload.wave)     this.wave     = { ...this.wave, ...payload.wave }
      if (payload.pressure) this.pressure = { ...this.pressure, ...payload.pressure }
      if (payload.map)      this.map      = {
        ...this.map,
        ...payload.map,
        extent: Array.isArray(payload.map.extent) && payload.map.extent.length === 4
          ? payload.map.extent.map(Number)
          : [...this.map.extent],
      }
    },
    async load() {
      const m_id = sessionStorage.m_id
      if (!m_id) return
      this.loading = true
      this.error = ''
      try {
        const data = await fetchUserSettings(m_id)
        this.applyServerData(data)
        this.loaded = true
      } catch (err) {
        this.error = err?.message || '설정을 불러오지 못했습니다.'
      } finally {
        this.loading = false
      }
    },
    // 내부 flat 상태 → 레거시 nested 응답 형식으로 변환 (set_setting 의 입력으로 사용)
    toServerPayload(payload) {
      const p = payload || {}
      const out = {}
      // 현재 활성 탭 — 모달이 마지막 본 탭 인덱스 (없으면 0)
      out.tab_idx = p.tab_idx ?? 0
      // map_name/map_type/extent — 평탄 키로
      if (p.map) {
        if (p.map.name != null) out.map_name = p.map.name
        if (p.map.type != null) out.map_type = p.map.type
        if (Array.isArray(p.map.extent)) out.extent = p.map.extent.map(Number)
      }
      // wave: nested 구조로 복원
      if (p.wave) {
        const w = p.wave
        out.wave = {
          frequency: { x: Number(w.freqXMax) || 0, y: Number(w.freqYMax) || 0 },
          color:  w.otherColor || '',
          color1: w.leakColor  || '',
          wave_energy: {
            start_size: Number(w.energyMin)   || 0,
            end_size:   Number(w.energyMax)   || 0,
            fix_size:   Number(w.energyFixed) || 0,
          },
          visible_wave_energy: !!w.valueVisible,
        }
      }
      // pressure: nested 구조로 복원
      if (p.pressure) {
        const ps = p.pressure
        out.pressure = {
          color: ps.color || '',
          legend: {
            value:      ps.value || 'avg',
            start_size: Number(ps.min) || 0,
            end_size:   Number(ps.max) || 0,
          },
          visible_value: !!ps.valueVisible,
        }
      }
      // layers: 내부 key → {code, f_type, name, visible, color, daum_minzoom, bing_minzoom}
      //   bing_minzoom 은 SettingsModal Layers 탭의 OSM 컬럼이 편집하는 값
      if (Array.isArray(p.layers)) {
        out.layers = p.layers.map(l => {
          const cf = KEY_TO_CODE[l.key] || {}
          const bing = l.bing_minzoom != null ? Number(l.bing_minzoom) : Number(l.zoom)
          const daum = l.daum_minzoom != null ? Number(l.daum_minzoom) : Number(l.zoom)
          return {
            ...(cf.code != null   ? { code: cf.code }   : {}),
            ...(cf.f_type != null ? { f_type: cf.f_type } : {}),
            name:    l.label || l.key,
            visible: !!l.visible,
            color:   l.color,
            daum_minzoom: daum,
            bing_minzoom: bing,
          }
        })
      }
      return out
    },
    async save(payload) {
      const m_id = sessionStorage.m_id
      this.applyLocal(payload)
      try {
        // 서버는 레거시 nested 형식을 기대함 — 변환 후 전송
        await saveUserSettings(m_id, this.toServerPayload(payload))
      } catch (err) {
        this.error = err?.message || '저장에 실패했습니다.'
        throw err
      }
    },
  },
})
